import React, { useState, useEffect } from 'react';
import { Button, message, Select, Tag, Tooltip, notification } from 'antd';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { currencySignsObj } from '@skygroup/shared/utils/constants';
import useGetGrossPrice from '../../../../hooks/useGetGrossPrice';
import {
  FaCheckCircle,
  FaClock,
  FaCrown,
  FaTruck,
  FaPrint,
  FaUsers,
  FaHourglassHalf,
} from 'react-icons/fa';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import './ticketCard.less';
import {
  getFirstAvailableQuantity,
  getMaxQuantity,
  getQuantityOptions,
  getFirstAvailableDeliveryMethod,
} from './helpers';
import { HiOutlineTicket } from 'react-icons/hi';

interface TicketCardProps {
  ticket: any;
  event: any;
  initialQuantity?: number;
  initialDeliveryMethod?: any;
  isPackageView?: boolean;
  isDynamicPackage?: boolean;
  isPreviewMode?: boolean;
  handleSelectTicket?: ({
    ticket,
    quantity,
    deliveryMethod,
  }: {
    ticket: any;
    quantity: number;
    deliveryMethod: any;
  }) => void;
  handleRemoveTicket?: (ticketId: number) => void;
  onQuantityChange?: (quantity: number, ticketId?: number) => void;
  onDeliveryMethodChange?: (deliveryMethod: any, ticketId?: number) => void;
  renderButtonInside?: boolean;
  isSelected?: boolean;
  hasAnySelected?: boolean;
  totalSelectedCount?: number;
  compactView?: boolean;
}

const formatCategoryRestriction = (restriction: unknown): string => {
  if (typeof restriction === 'string') {
    return restriction;
  }

  if (restriction && typeof restriction === 'object') {
    const record = restriction as { text?: string; description?: string };
    return record.text ?? record.description ?? String(restriction);
  }

  return String(restriction);
};

const TicketCard: React.FC<TicketCardProps> = ({
  ticket,
  event,
  initialQuantity,
  initialDeliveryMethod,
  isPackageView = false,
  isDynamicPackage = false,
  isPreviewMode = false,
  handleSelectTicket,
  handleRemoveTicket,
  onQuantityChange,
  onDeliveryMethodChange,
  renderButtonInside = false,
  isSelected = false,
  hasAnySelected = false,
  totalSelectedCount = 0,
  compactView = false,
}) => {
  const {
    sportsAndEvents: t_sportsAndEvents,
    cart: t_cart,
    general: t_general,
  } = useSelector((data: RootState) => data.config.translations);
  const getGrossPrice = useGetGrossPrice();
  const { siteConfig } = useSelector((data: RootState) => data.config.config);

  // Initialize state with props or defaults
  const [selectedQuantity, setSelectedQuantity] = useState<number>(
    initialQuantity !== undefined
      ? initialQuantity
      : getFirstAvailableQuantity(ticket)
  );
  const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState<any>(
    initialDeliveryMethod !== undefined
      ? initialDeliveryMethod
      : ticket.deliveryMethods && ticket.deliveryMethods.length > 0
      ? ticket.deliveryMethods[0]
      : null
  );

  // Update state when props change
  useEffect(() => {
    if (initialQuantity !== undefined) {
      setSelectedQuantity(initialQuantity);
    }
  }, [initialQuantity]);

  useEffect(() => {
    if (initialDeliveryMethod !== undefined) {
      setSelectedDeliveryMethod(initialDeliveryMethod);
    }
  }, [initialDeliveryMethod]);

  useEffect(() => {
    const quantities = ticket?.availableSellingQuantities;

    if (!Array.isArray(quantities) || quantities.length === 0) {
      return;
    }

    if (quantities.includes(selectedQuantity)) {
      return;
    }

    const closestQuantity = quantities.reduce((closest: number, quantity: number) =>
      Math.abs(quantity - selectedQuantity) < Math.abs(closest - selectedQuantity)
        ? quantity
        : closest
    , quantities[0]);

    setSelectedQuantity(closestQuantity);
    onQuantityChange?.(closestQuantity, ticket.id);
  }, [
    onQuantityChange,
    selectedQuantity,
    ticket?.availableSellingQuantities,
    ticket?.id,
  ]);

  const handleQuantityChange = (quantity: number) => {
    setSelectedQuantity(quantity);

    if (quantity > 0) {
      setSelectedQuantity(quantity);
      onQuantityChange?.(quantity, ticket.id);
    } else {
      message.error(t_sportsAndEvents?.quantityCannotBeZero);
    }
  };

  const handleDeliveryMethodChange = (deliveryMethodId: number) => {
    const method = ticket.deliveryMethods.find(
      (dm: any) => dm.id === deliveryMethodId
    );

    if (method) {
      setSelectedDeliveryMethod(method);
      onDeliveryMethodChange?.(method, ticket.id);
    } else {
      message.error(t_sportsAndEvents?.deliveryMethodNotFound);
    }
  };

  const getButtonText = () => {
    if (isDynamicPackage) {
      if (isSelected) {
        // For selected tickets in dynamic package mode, show "Selected" or "Remove"
        if (totalSelectedCount > 1) {
          return t_cart.remove;
        }
        return t_general.selected;
      } else if (hasAnySelected) {
        // For unselected tickets when others are selected in radio mode, show "Select"
        // This indicates switching behavior (radio button style)
        return t_cart.select;
      } else {
        // First ticket selection
        return t_cart.select;
      }
    } else if (isSelected) {
      return t_general.selected;
    } else if (isPackageView) {
      return t_cart.select;
    } else {
      return t_cart.addToCart;
    }
  };

  const onButtonClick = () => {
    if (isDynamicPackage && isSelected) {
      // Handle removal in dynamic package mode
      if (totalSelectedCount <= 1) {
        message.warning(t_sportsAndEvents?.atLeastOneTicketMustBeSelected);
        return;
      }
      handleRemoveTicket?.(ticket.id);
    } else {
      // Handle selection/addition
      handleSelectTicket?.({
        ticket,
        quantity: selectedQuantity,
        deliveryMethod: selectedDeliveryMethod,
      });
    }
  };

  const renderButton = () => {
    const buttonText = getButtonText();
    const isRemoveButton =
      isDynamicPackage && isSelected && totalSelectedCount > 1;

    return (
      <Button
        type={isRemoveButton ? 'default' : 'primary'}
        size="small"
        onClick={onButtonClick}
        className="btn-style-5"
        danger={isRemoveButton}
        disabled={
          !ticket.id ||
          ticket?.availableSellingQuantities?.length === 0 ||
          (isSelected && !isDynamicPackage) ||
          (isDynamicPackage && isSelected && totalSelectedCount <= 1)
        }
      >
        {buttonText}
      </Button>
    );
  };

  const currencySign =
    currencySignsObj?.[
      ticket.currency?.toLowerCase() as keyof typeof currencySignsObj
    ] ?? ticket.currency;

  const unitPrice = getGrossPrice(
    ticket.price,
    isDynamicPackage
      ? siteConfig?.shareB2C?.dp
      : ticket?.agent_additional_info?.suggested_commission,
    0
  );

  const deliveryPrice = selectedDeliveryMethod?.price ?? 0;
  const totalPrice = unitPrice * selectedQuantity + deliveryPrice;

  const sittingArrangementsText = ticket.sittingArrangements?.trim();
  const isAllTogether = sittingArrangementsText
    ?.toLowerCase()
    .includes('all together');
  const categoryRestrictions = Array.isArray(ticket.categoryRestrictions)
    ? ticket.categoryRestrictions.filter(Boolean)
    : [];

  return (
    <div>
      <div
        className={`ticket-item ${
          !handleSelectTicket || isPreviewMode ? '' : 'hover-able'
        } ${isSelected ? 'selected' : ''}`}
      >
        <div className="ticket-info">
          <div className="ticket-name-row">
            <h3 className="ticket-name">
              {ticket.categoryName || ticket.name}
            </h3>
            <div className="category-sections-text">
              {ticket.categorySections?.map((key: any, ii: number) => (
                <span key={key}>
                  {ii > 0 ? ' | ' : ''}
                  {key}
                </span>
              ))}
            </div>
          </div>

          <div className="ticket-tags">
            {ticket.splittedCategoryName?.main && (
              <Tag>{ticket.splittedCategoryName.main}</Tag>
            )}
            {ticket.splittedCategoryName?.secondary && (
              <Tag>{ticket.splittedCategoryName.secondary}</Tag>
            )}
            {ticket.immediateConfirmation && (
              <Tag color="green" icon={<FaCheckCircle />}>
                {t_sportsAndEvents?.instantConfirmation}
                {ticket.immediateConfirmationText && (
                  <Tooltip title={ticket.immediateConfirmationText}>
                    <AiOutlineInfoCircle
                      size={14}
                      className="tag-hint-icon"
                      style={{ marginLeft: '4px', cursor: 'pointer' }}
                    />
                  </Tooltip>
                )}
              </Tag>
            )}
            {ticket.immediateConfirmation === false && (
              <Tag color="orange" icon={<FaHourglassHalf />}>
                {t_sportsAndEvents?.offlineStock}
                {ticket.immediateConfirmationText && (
                  <Tooltip title={ticket.immediateConfirmationText}>
                    <AiOutlineInfoCircle
                      size={14}
                      className="tag-hint-icon"
                      style={{ marginLeft: '4px', cursor: 'pointer' }}
                    />
                  </Tooltip>
                )}
              </Tag>
            )}
            {sittingArrangementsText && (
              <Tag
                color={isAllTogether ? 'green' : 'orange'}
                icon={<FaUsers />}
              >
                {sittingArrangementsText}
              </Tag>
            )}
            {ticket.onHold24Hours?.status && (
              <Tag color="orange" icon={<FaClock />}>
                {t_sportsAndEvents?.hold24Hours}
                {ticket.onHold24Hours?.publicMessage && (
                  <Tooltip title={ticket.onHold24Hours.publicMessage}>
                    <AiOutlineInfoCircle
                      size={14}
                      className="tag-hint-icon"
                      style={{ marginLeft: '4px', cursor: 'pointer' }}
                    />
                  </Tooltip>
                )}
              </Tag>
            )}
            {ticket.isVipPackage && (
              <Tag color="gold" icon={<FaCrown />}>
                {t_sportsAndEvents?.vipPackage}
              </Tag>
            )}
          </div>

          {categoryRestrictions.length > 0 && (
            <div className="ticket-restrictions">
              <ExclamationCircleOutlined className="ticket-restrictions-icon" />
              <div className="ticket-restrictions-list">
                {categoryRestrictions.map((restriction: unknown, index: number) => (
                  <div key={index}>{formatCategoryRestriction(restriction)}</div>
                ))}
              </div>
            </div>
          )}

          {ticket.description && (
            <p className="ticket-description">{ticket.description}</p>
          )}
          {compactView ? <HiOutlineTicket size={32} /> : (
            <>
              {ticket.availableSellingQuantities !== undefined && (
                <div className="ticket-availability-text">
                  {getMaxQuantity(ticket)} {t_sportsAndEvents?.ticketsAvailable}
                </div>
              )}
              <div className="ticket-price-calc">
                <Tooltip
                  title={
                    <div className="price-breakdown-tooltip">
                      <div className="breakdown-row">
                        <span>{t_sportsAndEvents?.quantity}</span>
                        <span>{selectedQuantity}</span>
                      </div>
                      <div className="breakdown-row">
                        <span>{t_sportsAndEvents?.pricePerTicket}</span>
                        <span>
                          {currencySign} {unitPrice}
                        </span>
                      </div>
                      {deliveryPrice > 0 && (
                        <div className="breakdown-row">
                          <span>{t_sportsAndEvents?.deliveryMethod}</span>
                          <span>
                            {currencySign} {deliveryPrice}
                          </span>
                        </div>
                      )}
                      <div className="breakdown-row breakdown-total">
                        <span>{t_sportsAndEvents?.totalPrice}</span>
                        <span>
                          {currencySign} {totalPrice}
                        </span>
                      </div>
                    </div>
                  }
                >
                  <div className="price-equation">
                    <span className="calc-qty">{selectedQuantity}</span>
                    <span className="calc-op">×</span>
                    <span className="calc-value">
                      {currencySign} {unitPrice}
                    </span>
                    {deliveryPrice > 0 && (
                      <>
                        <span className="calc-op">+</span>
                        <span className="calc-value calc-delivery">
                          {currencySign} {deliveryPrice}
                        </span>
                      </>
                    )}
                    <span className="calc-op">=</span>
                    <span className="calc-total">
                      {currencySign} {totalPrice}
                    </span>
                  </div>
                </Tooltip>
                <div className="price-total-label">
                  {t_sportsAndEvents?.totalPrice}
                </div>
              </div>
              {ticket.availableQuantity !== undefined && (
                <div className="ticket-availability">
                  {t_sportsAndEvents?.available}: {ticket.availableQuantity}
                </div>
              )}
            </>
          )}
        </div>

        {compactView ? null : (
          <div className="ticket-selector">
            <div className="selector-group">
              <div className="selector-item">
                <label className="selector-label">
                  {t_sportsAndEvents?.quantity}
                </label>
                <Select
                  value={selectedQuantity}
                  onChange={handleQuantityChange}
                  options={getQuantityOptions(
                    ticket,
                    isDynamicPackage ? initialQuantity : undefined
                  )}
                  className="ticket-quantity-select"
                  placeholder={t_sportsAndEvents?.selectQuantity}
                />
              </div>

              {ticket.deliveryMethods && ticket.deliveryMethods.length > 0 && (
                <div className="selector-item delivery-method-selector">
                  <label className="selector-label">
                    {t_sportsAndEvents?.deliveryMethod}
                  </label>
                  {ticket.deliveryMethods.length === 1 ? (
                    // If only one delivery method, display it directly
                    <div className="delivery-method-display">
                      {ticket.deliveryMethods[0].name === 'E-Ticket' ? (
                        <FaPrint className="delivery-icon" />
                      ) : (
                        <FaTruck className="delivery-icon" />
                      )}{' '}
                      {ticket.deliveryMethods[0].name}{' '}
                      {ticket.deliveryMethods[0].price > 0 &&
                        `(+${
                          currencySignsObj?.[
                            ticket.currency?.toLowerCase() as keyof typeof currencySignsObj
                          ] ?? ticket.currency
                        }${ticket.deliveryMethods[0].price})`}
                    </div>
                  ) : (
                    // If multiple delivery methods, show select dropdown
                    <Select
                      value={
                        selectedDeliveryMethod?.id ||
                        getFirstAvailableDeliveryMethod(ticket)
                      }
                      onChange={handleDeliveryMethodChange}
                      className="delivery-method-select"
                      placeholder={t_sportsAndEvents?.selectDeliveryMethod}
                    >
                      {ticket.deliveryMethods.map((method: any) => (
                        <Select.Option key={method.id} value={method.id}>
                          {method.name === 'E-Ticket' ? (
                            <FaPrint className="delivery-icon" />
                          ) : (
                            <FaTruck className="delivery-icon" />
                          )}{' '}
                          {method.name}{' '}
                          {method.price > 0 &&
                            `(+${
                              currencySignsObj?.[
                                ticket.currency?.toLowerCase() as keyof typeof currencySignsObj
                              ] ?? ticket.currency
                            }${method.price})`}
                        </Select.Option>
                      ))}
                    </Select>
                  )}
                </div>
              )}

              {renderButtonInside && (
                <div
                  className="render-button-inside"
                  style={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                  {renderButton()}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {!renderButtonInside && (
        <div
          style={{
            display: !handleSelectTicket || isPreviewMode ? 'none' : 'flex',
            justifyContent: 'flex-end',
            marginTop: '12px',
          }}
        >
          {renderButton()}
        </div>
      )}
    </div>
  );
};

export default TicketCard;
